import React, { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHead, Panel } from "@/pages/vendor/Shell";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PLAN_KEYS = [["trial", "Free trial"], ["pro", "Pro"], ["starter", "No active plan"]];

function VendorSheet({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const [d, setD] = useState<any>(null);
  const load = () => api.get(`/admin/vendors/${id}`).then((r) => setD(r.data));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const patch = async (body: any) => {
    try { await api.patch(`/admin/vendors/${id}`, body); toast.success("Vendor updated"); load(); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg" data-testid="admin-vendor-sheet">
        <SheetHeader><SheetTitle>{d?.vendor.name || "Vendor"}</SheetTitle></SheetHeader>
        {d && (
          <div className="mt-4 space-y-5 text-sm">
            <p className="text-muted-foreground">{d.vendor.category} · {d.leads} leads · {d.events.length} events</p>
            <div className="space-y-2">
              <p className="font-semibold">Plan</p>
              <Select value={d.vendor.plan} onValueChange={(v) => patch({ plan: v })}>
                <SelectTrigger data-testid="admin-vendor-plan-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PLAN_KEYS.map(([p, label]) => <SelectItem key={p} value={p}>{label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <label className="flex items-center justify-between rounded-sm border p-3">
              <span><b>Suspend vendor</b><br /><span className="text-xs text-muted-foreground">Blocks dashboard access and booth pages</span></span>
              <Switch checked={!!d.vendor.suspended} onCheckedChange={(v) => patch({ suspended: v })} data-testid="admin-vendor-suspend-switch" />
            </label>
            <div>
              <p className="mb-2 font-semibold">Team</p>
              {d.team.map((m: any) => <p key={m.id} className="text-xs">{m.name} · {m.email} · {m.staff_role}</p>)}
            </div>
            <div>
              <p className="mb-2 font-semibold">Payments</p>
              {d.transactions.length === 0 ? <p className="text-xs text-muted-foreground">No checkouts yet.</p> :
                d.transactions.map((t: any) => <p key={t.session_id} className="text-xs">{t.plan} · ${t.amount} · {t.payment_status} · {t.created_at.slice(0, 10)}</p>)}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function AdminVendors() {
  const [rows, setRows] = useState<any[]>([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const load = () => api.get("/admin/vendors", { params: { q: q || undefined } }).then((r) => setRows(r.data));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div>
      <PageHead eyebrow="Account management" title="Vendors" />
      <div className="relative mb-4 max-w-md"><Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input className="pl-9" placeholder="Search vendors" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-vendor-search" /></div>
      <Panel className="overflow-x-auto p-0" data-testid="admin-vendors-table">
        <table className="w-full text-sm">
          <thead className="bg-muted text-left text-xs uppercase text-muted-foreground"><tr><th className="p-3">Vendor</th><th className="p-3">Owner</th><th className="p-3">Plan</th><th className="p-3">Leads</th><th className="p-3">Status</th></tr></thead>
          <tbody className="divide-y">{rows.map((v) => (
            <tr key={v.id} onClick={() => setOpen(v.id)} className="cursor-pointer hover:bg-muted" data-testid={`admin-vendor-row-${v.id}`}>
              <td className="p-3 font-semibold">{v.name}</td><td className="p-3 text-xs">{v.owner_email}</td>
              <td className="p-3">{v.plan_info.name}</td><td className="p-3 font-mono">{v.leads}</td>
              <td className="p-3">{v.suspended ? <span className="text-rose-600">Suspended</span> : <span className="text-green-700">Active</span>}</td>
            </tr>
          ))}</tbody>
        </table>
      </Panel>
      {open && <VendorSheet id={open} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}
